<template>
    <div v-if="!baseStore.isLoading" class="container">
        <v-row>
            <v-col>
                <v-tabs v-if="showMpcAvvik && isToday" v-model="viewSelection" height="30" class="selection-tabs">
                    <v-tab key="mpc" data-id="mpc"> MPC </v-tab>
                    <v-tab key="avvik" data-id="avvik" @click="navigateAvvik()"> Avvik </v-tab>
                </v-tabs>
            </v-col>
            <v-col>
                <div class="date body-2 text-right mr-2">
                    {{ date(baseStore.selectedDate) }}
                </div>
            </v-col>
        </v-row>

        <vessel-name v-if="showVesselName" :value="trip.vesselName" :read-only="readOnly" @update="updateVesselName($event)" />

        <v-row v-if="!baseStore.isOnline">
            <v-col>
                <v-alert type="error" variant="outlined" icon="mdi-close-circle-outline">
                    <span class="offline-alert-content">
                        Mistet internett tilkobling. Sist oppdatert {{ FormatDateTime(tripsStore.tripsUpdatedAt) }}
                    </span>
                </v-alert>
            </v-col>
        </v-row>

        <div
            v-if="trip && trip.passingTimes && trip.passingTimes.length !== 0 && tripsStore.singleViewMode"
            class="v-container form-switch"
        >
            <v-btn :class="{ 'bg-secondary': counterView === 'compact' }" data-id="form-view-compact" @click="counterView = 'compact'">
                Compact
            </v-btn>
            <v-btn :class="{ 'bg-secondary': counterView === 'list' }" data-id="form-view-list" @click="counterView = 'list'"> List </v-btn>
        </div>

        <trip-info v-bind="tripInfo" class="v-container" />

        <div v-if="trip && trip.passingTimes && trip.passingTimes.length !== 0">
            <compact-counter-stop
                v-if="tripsStore.singleViewMode && counterView === 'compact'"
                :options="passingTime"
                :no-show-booking="tripsStore.bookingNoShowList"
                :totals="totalForStop(passingTime)"
                :disable-remove="isFirst || (trip.autoSwitchTripsEnabled && isLast)"
                :disable-add="isLast"
                :entry-mode="baseStore.entryMode"
                :editable="!isLast"
                :read-only="readOnly"
                :view="counterView"
                :has-next-stop="checkNextStop()"
                @update="addOrUpdatePassingTime($event)"
                @no-passengers="noPassengers(passingTime)"
                @delete-no-show="openDeleteNoShowDialog"
            />
            <counter-stop
                v-else-if="tripsStore.singleViewMode && counterView === 'list'"
                :options="passingTime"
                :no-show-booking="tripsStore.bookingNoShowList"
                :totals="totalForStop(passingTime)"
                :disable-remove="isFirst || (trip.autoSwitchTripsEnabled && isLast)"
                :disable-add="isLast"
                :entry-mode="baseStore.entryMode"
                :editable="!isLast"
                :read-only="readOnly"
                :inline="false"
                @update="addOrUpdatePassingTime($event)"
                @no-passengers="noPassengers(passingTime)"
                @delete-no-show="openDeleteNoShowDialog"
            />
            <counter-stop
                v-for="(pt, index) in trip.passingTimes"
                v-else
                :key="index"
                :options="pt"
                :no-show-booking="tripsStore.bookingNoShowList"
                :totals="totalForStop(pt)"
                inline
                :disable-remove="index === 0 || (trip.autoSwitchTripsEnabled && index === trip.passingTimes.length - 1)"
                :disable-add="index === trip.passingTimes.length - 1"
                :entry-mode="baseStore.entryMode"
                :editable="index < trip.passingTimes.length - 1"
                :read-only="readOnly"
                @update="addOrUpdatePassingTime($event)"
                @no-passengers="noPassengers(pt)"
                @delete-no-show="openDeleteNoShowDialog"
            />
            <counter-nav
                v-if="tripsStore.singleViewMode"
                :prev-name="prevStop ? prevStop.name : null"
                :next-name="nextStop ? nextStop.name : null"
                @prev="goToPrevStop"
                @next="goToNextStop"
            />
            <counter-footer
                ref="counterFooter"
                :interchange-trip-id="interchangeTripId"
                :interchange-passangers="total.passengers"
                :is-bus="!tripsStore.singleViewMode"
                :read-only="readOnly"
                :disable-complete="!!completionError"
                :validation-message="completionError"
                @complete-trip="completeTrip"
                @interchange-trip="interchangeTrip"
                @cancel-trip="cancelTrip"
                @go-back="goBack"
            />
        </div>
        <trip-dialog
            v-model="showTripCompleted"
            dialog-text="Denne turen er allerede registrert."
            button-text="Lås opp for å redigere"
            @back-to-departures="navigateBack()"
            @input="startExistingTrip()"
        />
        <trip-dialog
            v-model="showTripStartedDialog"
            dialog-text="Denne reisen har allerede blitt startet av en annen. Fortsett?"
            button-text="Ja"
            @back-to-departures="navigateBack()"
            @input="startExistingTrip()"
        />
        <no-show-dialog v-model="noShowDialog" @register-noshow="registerNoShow" @close="closeNoShowDialog" />
        <v-dialog :model-value="deleteDialog" class="no-show-dialog" max-width="300" persistent>
            <v-card>
                <v-card-text class="pa-10">
                    <p class="body-1 font-weight-bold text-center pb-3">Tilbakestill status for manglende oppmøte</p>
                    <p class="pb-3 text-center">Er du sikker på at du vil tilbakestille statusen?</p>

                    <v-row align="center">
                        <v-col>
                            <v-btn color="primary" large block data-id="no-show-delete-button" @click="deleteNoShow">Tilbakestill</v-btn>
                        </v-col>
                        <v-col>
                            <v-btn large block @click="closeDeleteDialog">Avbryt</v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup lang="ts">
import VesselName from '@/features/trips/VesselName.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TripInfo from '@/features/trips/TripInfo.vue';
import TripDialog from '@/features/trips/TripDialog.vue';
import CounterStop from '@/features/trips/counters/CounterStop.vue';
import CompactCounterStop from '@/features/trips/counters/CompactCounterStop.vue';
import CounterNav from '@/features/trips/counters/CounterNav.vue';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import NoShowDialog from '@/features/trips/NoShowDialog.vue';
import CounterFooter from '@/features/trips/counters/CounterFooter.vue';
import useNavigate from '@/plugins/navigate';
import { useEmitter, Events } from '@/plugins/emitter';
import { TripStatus } from '@/features/trips/TripStatus';
import { BookingNoShowResponseDto, BookingNoShowType } from '@/features/trips/BookingNoShowModels';
import { PassingTime } from '@/features/trips/PassingTime';
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { Views, ViewIndex } from '@/helpers/ViewSwitcher';
import { useBaseStore } from '@/features/shared/baseStore';
import { useLinesStore } from '@/features/lines/linesStore';
import { useSettingsStore } from '@/features/shared/settingsStore';
import { useTripsStore } from '@/features/trips/tripsStore';
import {
    getPassingTimeTotalsOnboard,
    getPassingTimeTotalsOnboardForStop,
    hasNegativeOnboard
} from '@/features/trips/PassingTimeTotalCalculator';
import { TransportType } from '@/features/shared/models/TransportType';
import { startWatchingPosition, stopWatchingPosition } from '@/features/shared/lastKnownPosition';
import { FormatDateTime } from '@/helpers/DateFormatHelpers';
import { useDateTimeFilters } from '@/helpers/useDateTimeFilters';

const showTripCompleted = ref(false);
const showTripStartedDialog = ref(false);
let syncInterval;
const syncTimeout = 60000; // 1 min
const viewSelection = ref(0);
const { navigate } = useNavigate();
const counterFooter = ref<InstanceType<typeof CounterFooter>>(null);
const route = useRoute();
const router = useRouter();
const eventbus = useEmitter();
const counterView = ref('compact');
const { date } = useDateTimeFilters();

const baseStore = useBaseStore();
const settingsStore = useSettingsStore();
const tripsStore = useTripsStore();
const linesStore = useLinesStore();

const noShowDialog = ref(false);
const currentStop = ref<PassingTime | null>(null);
const deleteDialog = ref(false);
const currentBookingNoShow = ref<BookingNoShowResponseDto | null>(null);

function noPassengers(passingTime: PassingTime) {
    noShowDialog.value = true;
    currentStop.value = passingTime;
}

function openDeleteNoShowDialog(val: BookingNoShowResponseDto) {
    deleteDialog.value = true;
    currentBookingNoShow.value = val;
}

async function deleteNoShow() {
    await tripsStore.deleteNoShow(currentBookingNoShow.value);
    closeDeleteDialog();
}

function closeDeleteDialog() {
    deleteDialog.value = false;
    currentBookingNoShow.value = null;
}

async function registerNoShow(status: BookingNoShowType) {
    await tripsStore.registerNoShow(trip.value, currentStop.value, status, baseStore.calendarDay);
    closeNoShowDialog();
}

function closeNoShowDialog() {
    noShowDialog.value = false;
    currentStop.value = null;
}

interface Props {
    lineNumber: string;
    tripId: string;
    stopIndex: number | string; // required
}
const props = defineProps<Props>();

eventbus.emitter.on(Events.dateChanged, fetchTripsSync);
eventbus.emitter.emit(Events.menuUpdate, 'trip');

const isToday = computed(() => {
    return baseStore.isToday;
});

const showMpcAvvik = computed(() => {
    return settingsStore.settings && settingsStore.showMpcAvvik;
});

const showMpc = computed(() => {
    return settingsStore.settings && settingsStore.showMpc;
});

const showAvvik = computed(() => {
    return settingsStore.settings && settingsStore.showAvvik;
});

const passingTimeIndexNumber = computed(() => {
    return Number(props.stopIndex);
});

const isFirst = computed(() => {
    return passingTimeIndexNumber.value === 0;
});

const isLast = computed(() => {
    return trip.value.passingTimes && passingTimeIndexNumber.value === trip.value.passingTimes.length - 1;
});

const passingTime = computed(() => {
    return trip.value.passingTimes ? trip.value.passingTimes[passingTimeIndexNumber.value] : null;
});

const trip = computed(() => {
    return tripsStore.trips.find(x => x.tripId === props.tripId);
});

const interchangeTripId = computed(() => {
    return trip.value?.tripInterchange?.nextTripId;
});

const currentLine = computed(() => {
    return linesStore.getLineByNumber(baseStore.lineNumber);
});

const tripInfo = computed(() => {
    if (!trip.value || !currentLine.value) {
        return {
            name: null,
            origin: null,
            destination: null,
            originDeparture: null
        };
    }

    return {
        name: currentLine.value.name,
        origin: trip.value.originQuayName,
        destination: trip.value.destination,
        originDeparture: trip.value.originDeparture
    };
});

const { searchFilters } = storeToRefs(tripsStore);

watch(
    () => trip.value,
    async val => {
        if (val) {
            searchFilters.value.tripId = val.tripId;
            searchFilters.value.operatingDay = val.operatingDay;
            await tripsStore.loadBookingNoShows(1);
        }
    }
);

const readOnly = computed(() => {
    return baseStore.isFutureDay || settingsStore.isReadOnly;
});

const showVesselName = computed(() => {
    return trip.value && trip.value.transportType === TransportType.Water;
});

const prevStop = computed(() => {
    if (passingTimeIndexNumber.value <= 0) return null;

    return {
        name: trip.value.passingTimes[passingTimeIndexNumber.value - 1].stopName,
        route: {
            name: 'trip',
            params: {
                ...route.params,
                stopIndex: String(passingTimeIndexNumber.value - 1)
            },
            query: {
                ...route.query
            }
        }
    };
});

const nextStop = computed(() => {
    // if autoswitch is enabled, set link to next trip if it exists
    if (nextTripId.value && !readOnly.value) {
        return {
            name: 'Start ny tur',
            isNewTrip: true,
            route: {
                name: 'trip',
                params: {
                    ...route.params,
                    tripId: nextTripId.value,
                    stopIndex: '0'
                },
                query: {
                    ...route.query
                }
            }
        };
    }

    const nextPassingTimeIndex = passingTimeIndexNumber.value + 1;
    const nextPassingTime = trip.value.passingTimes[nextPassingTimeIndex];

    // return the next stop if it exists, otherwise return null
    return nextPassingTime
        ? {
              name: nextPassingTime.stopName,
              isNewTrip: false,
              route: {
                  name: 'trip',
                  params: {
                      ...route.params,
                      stopIndex: String(nextPassingTimeIndex)
                  },
                  query: {
                      ...route.query
                  }
              }
          }
        : null;
});

function checkNextStop() {
    return nextStop.value ? !nextStop.value.isNewTrip : false;
}

const nextTripId = computed(() => {
    // nextTripId should only show up when we're at the last stop
    if (passingTimeIndexNumber.value >= trip.value.passingTimes.length - 1 && trip.value.autoSwitchTripsEnabled) {
        return trip.value.nextTripId;
    }

    return null;
});

const lineName = computed(() => {
    return currentLine.value?.name ?? '';
});

const total = computed(() => {
    if (!trip.value.passingTimes) {
        return {
            passengers: 0,
            allCars: 0
        };
    }

    return getPassingTimeTotalsOnboard(trip.value.passingTimes);
});

const completionError = computed(() => {
    if (readOnly.value) return null;

    return hasNegativeOnboard(total.value)
        ? 'Antall passasjerer eller kjøretøy ombord kan ikke være negativt. Korriger tellingen før du kan avslutte turen.'
        : null;
});

const totalForStop = computed(() => {
    return (passingTime: PassingTime) => {
        if (!trip.value.passingTimes) {
            return {
                passengers: 0,
                allCars: 0
            };
        }

        return getPassingTimeTotalsOnboardForStop(trip.value.passingTimes, passingTime);
    };
});

initialize();

async function initialize() {
    if (!showMpc.value && showAvvik.value) {
        navigateAvvik();
    }
    baseStore.lineNumber = props.lineNumber;
    await fetchTrips();
    baseStore.departuresPage = { pageName: `Avganger (${lineName.value})`, urlName: 'departures' };
    baseStore.tripPage = { pageName: 'Tur', urlName: 'trip' };

    if (!readOnly.value) {
        if (trip.value.tripStatus === TripStatus.Finished) {
            showTripCompleted.value = true;
        } else {
            showTripStartedDialog.value = !!trip.value?.startedByDifferentDriver;
        }
    }

    if (settingsStore.isReadOnly) {
        syncInterval = setInterval(syncTrips, syncTimeout);
    }

    await tripsStore.initMpcTrip({ trip: trip.value, calendarDay: baseStore.calendarDay, isReadOnly: settingsStore.isReadOnly });
}

function fetchTripsSync() {
    (async () => await fetchTrips())();
}

async function fetchTrips(backgroundSync = false) {
    await tripsStore.fetchTrips({
        lineNumber: baseStore.lineNumber,
        calendarDay: baseStore.calendarDay,
        backgroundSync
    });
}

async function syncTrips() {
    return fetchTrips(true);
}

function goToPrevStop() {
    if (prevStop.value) navigate(prevStop.value.route);
}

function goToNextStop() {
    if (!nextStop.value) return;

    // if autoswitch is enabled, set link to next trip
    if (passingTimeIndexNumber.value === trip.value.passingTimes.length - 1 && nextTripId.value) {
        // if we're on the last stop, and nextTripId exists, trigger completeTrip
        // unless completion is blocked by invalid onboard counts
        if (completionError.value) return;
        counterFooter.value.showCompleteTripDialog();
        return;
    }

    navigate(nextStop.value.route);
}

function flushPendingPassingTimes() {
    tripsStore.flushPassingTimes();
}

function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
        flushPendingPassingTimes();
    }
}

onMounted(() => {
    startWatchingPosition();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', flushPendingPassingTimes);
});

onBeforeUnmount(() => {
    flushPendingPassingTimes();
    stopWatchingPosition();
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('beforeunload', flushPendingPassingTimes);

    if (syncInterval) {
        clearInterval(syncInterval);
        syncInterval = undefined;
    }
    searchFilters.value.tripId = null;
    searchFilters.value.operatingDay = null;
    eventbus.emitter.off(Events.dateChanged, fetchTripsSync);
});

function navigateAvvik() {
    router.push({
        name: 'messages',
        params: { lineNumber: props.lineNumber, tripId: props.tripId },
        query: { token: router.currentRoute.value.query.token, view: Views[ViewIndex.avvik], date: route.query.date }
    });
}

async function goBack() {
    if (trip.value.lineNumber) {
        await navigate({ name: 'departures', query: { lineNumber: trip.value.lineNumber } });
    }
}

async function _completeTrip() {
    eventbus.emitter.emit('completeTrip');
    await tripsStore.completeTrip(trip.value);
    eventbus.emitter.emit(Events.notification, 'tripCompleted');
}

async function completeTrip() {
    await _completeTrip();

    // navigate to departures
    if (trip.value.lineNumber) {
        await navigate({ name: 'departures', params: { lineNumber: trip.value.lineNumber } });
    }
}

async function interchangeTrip() {
    if (interchangeTripId.value) {
        const interchangePassengers = total.value.passengers;
        await _completeTrip();

        const tripId = String(interchangeTripId.value);
        let tripToUpdate = tripsStore.trips.find(x => x.tripId === tripId);

        if (!tripToUpdate) {
            throw Error(`Trip by Id ${tripId} not found.`);
        }

        await tripsStore.initMpcTrip({ trip: tripToUpdate, calendarDay: baseStore.calendarDay, isReadOnly: settingsStore.isReadOnly });

        // Reload with initialized passing times
        tripToUpdate = tripsStore.trips.find(x => x.tripId === tripId);

        tripToUpdate.passingTimes[0].stopEventData.passengers = { in: interchangePassengers, out: 0 };
        await tripsStore.addOrUpdatePassingTime({ trip: tripToUpdate, passingTime: tripToUpdate.passingTimes[0] });

        await navigate({ name: 'trip', params: { lineNumber: tripToUpdate.lineNumber, tripId } });
    }
}

async function cancelTrip() {
    await tripsStore.cancelTrip(trip.value);
    eventbus.emitter.emit(Events.notification, 'tripCancelled');

    // navigate to departures
    if (trip.value.lineNumber) {
        await navigate({ name: 'departures', params: { lineNumber: trip.value.lineNumber } });
    }
}

async function addOrUpdatePassingTime(passingTime: PassingTime) {
    await tripsStore.addOrUpdatePassingTime({ trip: trip.value, passingTime });
}

async function updateVesselName(vesselName: string) {
    await tripsStore.updateVesselName({ trip: trip.value, vesselName });
}

async function startExistingTrip() {
    await tripsStore.startExistingTrip({ trip: trip.value });
    showTripStartedDialog.value = false;
    showTripCompleted.value = false;
}

function navigateBack() {
    navigate({ name: 'departures', params: { lineNumber: baseStore.lineNumber } });
    showTripStartedDialog.value = false;
    showTripCompleted.value = false;
}
</script>

<style lang="scss" scoped>
.form-switch {
    padding-top: 0;
    padding-bottom: 0;

    button {
        margin-right: 6px;
    }
}

.offline-alert-content {
    color: black;
    font-weight: bold;
}

@media (min-width: 960px) {
    .form-switch {
        padding-left: 0;
    }
}
</style>
@/services/emitter@/views/navigate
